import {PosaRedeemStore} from '../../utils/types';
/* eslint-disable react/no-danger */
import React from 'react';
import {
  getBalance,
  getCardValue,
  getDisplayNameForPlatform,
  getLocalizedPlatformName,
  isDirectPlatform,
} from '../../utils';

import ConfirmOverlayStyled from './ConfirmOverlayStyled';
import {localizedMessage} from '../message/Message';
import PosaTemplate from './PosaTemplate';
import PropTypes from 'prop-types';
import cx from 'classnames';
import BonusSection from './BonusSection';

const Row = ({name, value}) => {
  return (
    <div className="ecrp-confirm-detail-row">
      <span className="ecrp-confirm-detail-row-name">{name}</span>
      <span className="ecrp-confirm-detail-row-value">{value}</span>
    </div>
  );
};

Row.propTypes = {
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.func,
  ]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.func,
  ]),
};

export interface Props {
  posaRedeemStore: PosaRedeemStore;
  close: () => void;
  redeemFunc: () => void;
  resetFlow: () => void;
  showBonusSection: boolean;
}

const ConfirmOverlay = ({posaRedeemStore, close, redeemFunc, resetFlow, showBonusSection}) => {
  if (!posaRedeemStore || !posaRedeemStore.page || !posaRedeemStore.showConfirm) return null;
  const {
    platform,
    externalAuths,
    epicAccountName,
    offer,
    wallets,
    psnRegion,
    errorCode,
    bonusOffers,
  } = posaRedeemStore;
  const platformDisplayName = getDisplayNameForPlatform(platform, externalAuths);
  const deviceName = getLocalizedPlatformName(platform, localizedMessage);
  const cardAmountValue = getCardValue(offer);
  const accountBalance = getBalance(wallets, platform);
  const directPlatform = isDirectPlatform(platform);
  const importantMsg = localizedMessage(
    `epic.code.redemption.posa.page.redeem.confirm.important.${platform}`
  );
  const psnRegionName = localizedMessage(`epic.code.redemption.psn.region.${psnRegion}`);
  const errorMsg =
    errorCode && errorCode === localizedMessage(errorCode)
      ? localizedMessage('errors.com.epicgames.posa.unknown')
      : null;
  const confirmPlatformTitle = localizedMessage(
    'epic.code.redemption.posa.page.redeem.confirm.platform'
  ).replace('{0}', deviceName);
  const showbonus = showBonusSection && bonusOffers && bonusOffers.length;
  return (
    <ConfirmOverlayStyled className="ecr-posa-confirm-overlay">
      <div className={cx('ecrp-confirm-wrapper', {'with-bonus': showbonus})}>
        <span className="ecrp-confirm-close" onClick={close} role="presentation">
          X
        </span>
        <div className="ecrp-confirm-content">
          <PosaTemplate Tag="h1" string={confirmPlatformTitle} isTemplate />
          <div className="ecrp-confirm-box">
            <div className="ecrp-confirm-table">
              <h1>{localizedMessage('epic.code.redemption.posa.page.redeem.confirm.title')}</h1>
              <h3>{localizedMessage('epic.code.redemption.posa.page.redeem.confirm.subtitle')}</h3>
              <div className="ecrp-confirm-detail">
                <Row
                  name={localizedMessage(
                    'epic.code.redemption.posa.page.redeem.confirm.epicAccountName'
                  )}
                  value={epicAccountName}
                />
                {!directPlatform && platformDisplayName ? (
                  <Row
                    name={localizedMessage(
                      `epic.code.redemption.posa.page.redeem.confirm.platformAccountName.${platform}`
                    )}
                    value={platformDisplayName}
                  />
                ) : null}
                {deviceName ? (
                  <Row
                    name={localizedMessage('epic.code.redemption.posa.page.redeem.confirm.device')}
                    value={deviceName}
                  />
                ) : null}
                {platform === 'psn' && psnRegion ? (
                  <Row
                    name={localizedMessage(
                      'epic.code.redemption.posa.page.redeem.confirm.psnRegion'
                    )}
                    value={psnRegionName}
                  />
                ) : null}
                {accountBalance !== null && directPlatform ? (
                  <Row
                    name={
                      <PosaTemplate
                        posaRedeemStore={posaRedeemStore}
                        string={localizedMessage(
                          'epic.code.redemption.posa.page.redeem.confirm.money.balance'
                        )}
                      />
                    }
                    value={String(accountBalance)}
                  />
                ) : null}
                {cardAmountValue ? (
                  <Row
                    name={localizedMessage(
                      'epic.code.redemption.posa.page.redeem.confirm.money.card.balance'
                    )}
                    value={cardAmountValue}
                  />
                ) : null}
                {accountBalance !== null && directPlatform ? (
                  <Row
                    name={
                      <PosaTemplate
                        posaRedeemStore={posaRedeemStore}
                        string={localizedMessage(
                          'epic.code.redemption.posa.page.redeem.confirm.money.balance.after'
                        )}
                      />
                    }
                    value={String(
                      parseInt(accountBalance, 10) + parseInt(cardAmountValue || '0', 10)
                    )}
                  />
                ) : null}
              </div>
              <div className="ecrp-confirm-notice">
                {importantMsg ? (
                  <h4 className="ecn-important-msg">
                    <PosaTemplate
                      isTemplate
                      posaRedeemStore={posaRedeemStore}
                      string={importantMsg}
                    />
                  </h4>
                ) : null}
              </div>
            </div>
            <BonusSection
              show={showBonusSection}
              bonusOffers={bonusOffers}
              header="epic.code.redemption.posa.page.redeem.confirm.reward.title"
              subtitle="epic.code.redemption.posa.page.redeem.confirm.reward.subtitle"
              notice="epic.code.redemption.posa.page.redeem.confirm.reward.apply"
            />
          </div>
          <div className="ecrp-confirm-action">
            <button onClick={redeemFunc}>
              {localizedMessage('epic.code.redemption.posa.page.redeem.confirm')}
            </button>
            <button className="ecrp-confirm-reset" onClick={resetFlow}>
              {localizedMessage('epic.code.redemption.posa.page.redeem.confirm.startOver')}
            </button>
          </div>
          <div className={cx('confirm-error-msg', {opaque: errorCode})}>
            <span className="msg">{errorMsg}</span>
          </div>
        </div>
      </div>
    </ConfirmOverlayStyled>
  );
};

ConfirmOverlay.propTypes = {
  posaRedeemStore: PropTypes.object,
  close: PropTypes.func,
  redeemFunc: PropTypes.func,
  resetFlow: PropTypes.func,
  showBonusSection: PropTypes.bool,
};

export default ConfirmOverlay;
